import React from 'react';
import { dataTransferHasFiles } from '../../../functions/AzureFileUpload.Functions';

export interface FileDropTargetOptions {
  enabled: boolean;
  onFilesDropped: (files: File[]) => void;
}

export interface FileDropTargetHandlers {
  onDragEnter: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
}

export interface FileDropTarget {
  isDraggingFiles: boolean;
  dropTargetProps: FileDropTargetHandlers;
}

/**
 * Turns any element into a drop target for files coming from the OS.
 * Drags that do not carry files (rows, text, links, column resize...) are ignored,
 * so existing grid interactions keep working.
 */
export const useFileDropTarget = ({ enabled, onFilesDropped }: FileDropTargetOptions): FileDropTarget => {
  const [isDraggingFiles, setIsDraggingFiles] = React.useState(false);
  // dragenter/dragleave fire for every nested child: count them to know when the pointer really left.
  const depth = React.useRef(0);

  React.useEffect(() => {
    if (!enabled) {
      depth.current = 0;
      setIsDraggingFiles(false);
    }
  }, [enabled]);

  const onDragEnter = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!enabled || !dataTransferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      depth.current += 1;
      setIsDraggingFiles(true);
    },
    [enabled]
  );

  const onDragOver = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!enabled || !dataTransferHasFiles(event.dataTransfer)) return;
      // Required so the browser allows the drop instead of opening the file.
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    },
    [enabled]
  );

  const onDragLeave = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!enabled || !dataTransferHasFiles(event.dataTransfer)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsDraggingFiles(false);
    },
    [enabled]
  );

  const onDrop = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!enabled || !dataTransferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      depth.current = 0;
      setIsDraggingFiles(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) onFilesDropped(files);
    },
    [enabled, onFilesDropped]
  );

  return {
    isDraggingFiles,
    dropTargetProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
};
